import axiosClient from '@/config/api/axios'
import { INTEGRATION_CONNECTIONS } from '@/config/api/endpoints'
import { connectStartSchema } from '@/schemas/integrations/connection'

/** Thrown for 409, so the caller can say "already connected" rather than "failed". */
export class AlreadyConnectedError extends Error {
  constructor(connectionId) {
    super('already connected')
    this.name = 'AlreadyConnectedError'
    this.connectionId = connectionId
  }
}

/**
 * Begin hosted OAuth. Only the toolkit is sent — identity and the callback URL
 * are both server-side, so neither can be chosen here.
 *
 * `reconnect` repairs an existing connection instead of being refused as
 * already-connected. Without it an expired account is unreachable: there is no
 * refresh API, so re-consent is the only fix and 409 was the only answer.
 *
 * @param {{ toolkitSlug: string, reconnect?: boolean, workspaceId?: string, signal?: AbortSignal }} options
 */
export async function startConnection({ toolkitSlug, reconnect = false, workspaceId, signal }) {
  try {
    const { data } = await axiosClient.post(
      INTEGRATION_CONNECTIONS,
      { toolkit_slug: toolkitSlug, reconnect },
      { signal, headers: workspaceId ? { 'X-Workspace-Id': workspaceId } : undefined },
    )
    return connectStartSchema.parse(data)
  } catch (error) {
    if (error?.response?.status === 409) {
      throw new AlreadyConnectedError(error.response.data?.detail?.connection_id)
    }
    throw error
  }
}
